import React, { Suspense, lazy } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { Layout } from 'antd'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import AppLayout from './components/Layout/AppLayout'
import LoadingSpinner from './components/Common/LoadingSpinner'
import RouteGuard from './components/Common/RouteGuard'
import ErrorBoundary from './components/Common/ErrorBoundary'
import { PERMISSIONS } from './utils/permissions'

// Lazy load pages for code splitting
const LoginPage = lazy(() => import('./pages/LoginPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const LeadsPage = lazy(() => import('./pages/LeadsPage'))
const QuotesPage = lazy(() => import('./pages/QuotesPage'))
const BookingsPage = lazy(() => import('./pages/BookingsPage'))
const CustomersPage = lazy(() => import('./pages/CustomersPage'))
const TeamPage = lazy(() => import('./pages/TeamPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const VehiclesPage = lazy(() => import('./pages/VehiclesPage'))
const DealerManagementPage = lazy(() => import('./pages/DealerManagementPage'))
const PricingPage = lazy(() => import('./pages/PricingPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const AccessoriesPage = lazy(() => import('./pages/AccessoriesPage'))
const DealerInventoryPage = lazy(() => import('./pages/DealerInventoryPage'))
const PurchaseOrdersPage = lazy(() => import('./pages/PurchaseOrdersPage'))
const BankingManagementPage = lazy(() => import('./pages/BankingManagementPage'))
const BankManagementPage = lazy(() => import('./pages/BankManagementPage'))
const VehicleInventoryPage = lazy(() => import('./pages/VehicleInventoryPage'))
const AddStockPage = lazy(() => import('./pages/AddStockPage'))

// Accessories pages
const AddBrandPage = lazy(() => import('./pages/Accessories/AddBrandPage'))
const EditBrandPage = lazy(() => import('./pages/Accessories/EditBrandPage'))
const AddCategoryPage = lazy(() => import('./pages/Accessories/AddCategoryPage'))
const EditCategoryPage = lazy(() => import('./pages/Accessories/EditCategoryPage'))
const AddSubcategoryPage = lazy(() => import('./pages/Accessories/AddSubcategoryPage'))
const EditSubcategoryPage = lazy(() => import('./pages/Accessories/EditSubcategoryPage'))
const AddProductPage = lazy(() => import('./pages/Accessories/AddProductPage'))
const EditProductPage = lazy(() => import('./pages/Accessories/EditProductPage'))

// Leads pages
const AddLeadPage = lazy(() => import('./pages/Leads/AddLeadPage'))
const EditLeadPage = lazy(() => import('./pages/Leads/EditLeadPage'))

// Quotes pages
const AddQuotePage = lazy(() => import('./pages/Quotes/AddQuotePage'))
const EditQuotePage = lazy(() => import('./pages/Quotes/EditQuotePage'))

// Bookings pages
const AddBookingPage = lazy(() => import('./pages/Bookings/AddBookingPage'))
const EditBookingPage = lazy(() => import('./pages/Bookings/EditBookingPage'))

// Customers pages
const AddCustomerPage = lazy(() => import('./pages/Customers/AddCustomerPage'))
const EditCustomerPage = lazy(() => import('./pages/Customers/EditCustomerPage'))

// Team pages
const AddMemberPage = lazy(() => import('./pages/Team/AddMemberPage'))
const EditMemberPage = lazy(() => import('./pages/Team/EditMemberPage'))

// Purchase Orders pages
const AddPurchaseOrderPage = lazy(() => import('./pages/PurchaseOrders/AddPurchaseOrderPage'))
const EditPurchaseOrderPage = lazy(() => import('./pages/PurchaseOrders/EditPurchaseOrderPage'))

// Settings - Pincodes pages
const AddPincodePage = lazy(() => import('./pages/Settings/Pincodes/AddPincodePage'))
const EditPincodePage = lazy(() => import('./pages/Settings/Pincodes/EditPincodePage'))

// Settings - Registration Charges pages
const AddRegistrationChargePage = lazy(() => import('./pages/Settings/RegistrationCharges/AddRegistrationChargePage'))
const EditRegistrationChargePage = lazy(() => import('./pages/Settings/RegistrationCharges/EditRegistrationChargePage'))

// Settings - HSN pages
const AddHSNPage = lazy(() => import('./pages/Settings/HSN/AddHSNPage'))
const EditHSNPage = lazy(() => import('./pages/Settings/HSN/EditHSNPage'))

// Dealer Inventory pages
const AddInventoryPage = lazy(() => import('./pages/DealerInventory/AddInventoryPage'))
const EditInventoryPage = lazy(() => import('./pages/DealerInventory/EditInventoryPage'))
const PurchaseOrdersListPage = lazy(() => import('./pages/DealerInventory/PurchaseOrdersListPage'))
const AddEditPurchaseOrderPage = lazy(() => import('./pages/DealerInventory/AddEditPurchaseOrderPage'))
const ViewPurchaseOrderPage = lazy(() => import('./pages/DealerInventory/ViewPurchaseOrderPage'))
const ReceivePurchaseOrderPage = lazy(() => import('./pages/DealerInventory/ReceivePurchaseOrderPage'))
const ConvertToBillPage = lazy(() => import('./pages/DealerInventory/ConvertToBillPage'))
const BillsListPage = lazy(() => import('./pages/DealerInventory/BillsListPage'))
const ViewBillPage = lazy(() => import('./pages/DealerInventory/ViewBillPage'))
const RecordPaymentPage = lazy(() => import('./pages/DealerInventory/RecordPaymentPage'))

// Dealer Management pages
const AddDealerPage = lazy(() => import('./pages/DealerManagement/AddDealerPage'))
const EditDealerPage = lazy(() => import('./pages/DealerManagement/EditDealerPage'))

// Pricing pages
const EditPricePage = lazy(() => import('./pages/Pricing/EditPricePage'))

// Banking pages
const AddAccountPage = lazy(() => import('./pages/Banking/AddAccountPage'))
const EditAccountPage = lazy(() => import('./pages/Banking/EditAccountPage'))
const AddTransactionPage = lazy(() => import('./pages/Banking/AddTransactionPage'))
const EditTransactionPage = lazy(() => import('./pages/Banking/EditTransactionPage'))

// Vehicles pages
const AddVehicleBrandPage = lazy(() => import('./pages/Vehicles/Brands/AddBrandPage'))
const EditVehicleBrandPage = lazy(() => import('./pages/Vehicles/Brands/EditBrandPage'))
const AddVehicleModelPage = lazy(() => import('./pages/Vehicles/Models/AddModelPage'))
const EditVehicleModelPage = lazy(() => import('./pages/Vehicles/Models/EditModelPage'))
const AddVehicleVariantPage = lazy(() => import('./pages/Vehicles/Variants/AddVariantPage'))
const EditVehicleVariantPage = lazy(() => import('./pages/Vehicles/Variants/EditVariantPage'))
const AddVehicleColorPage = lazy(() => import('./pages/Vehicles/Colors/AddColorPage'))
const EditVehicleColorPage = lazy(() => import('./pages/Vehicles/Colors/EditColorPage'))
const AddVehicleStatePage = lazy(() => import('./pages/Vehicles/States/AddStatePage'))
const EditVehicleStatePage = lazy(() => import('./pages/Vehicles/States/EditStatePage'))

const { Content } = Layout

function AppContent() {
  const { isAuthenticated, authInitialized } = useAuth()

  if (!authInitialized) {
    return <LoadingSpinner />
  }

  if (!isAuthenticated) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <AppLayout>
        <Content style={{ margin: '8px', padding: 0, background: '#fff' }}>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* Dashboard - accessible to all authenticated users */}
              <Route path="/dashboard" element={
                <RouteGuard>
                  <DashboardPage />
                </RouteGuard>
              } />

              {/* Leads - accessible to all users */}
              <Route path="/leads" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <LeadsPage />
                </RouteGuard>
              } />
              <Route path="/leads/add" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <AddLeadPage />
                </RouteGuard>
              } />
              <Route path="/leads/:leadId/edit" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <EditLeadPage />
                </RouteGuard>
              } />

              {/* Quotes - accessible to all users */}
              <Route path="/quotes" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <QuotesPage />
                </RouteGuard>
              } />
              <Route path="/quotes/add" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <AddQuotePage />
                </RouteGuard>
              } />
              <Route path="/quotes/:quoteId/edit" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <EditQuotePage />
                </RouteGuard>
              } />

              {/* Bookings - accessible to all users */}
              <Route path="/bookings" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_BOOKINGS}>
                  <BookingsPage />
                </RouteGuard>
              } />
              <Route path="/bookings/add" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_BOOKINGS}>
                  <AddBookingPage />
                </RouteGuard>
              } />
              <Route path="/bookings/:bookingId/edit" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_BOOKINGS}>
                  <EditBookingPage />
                </RouteGuard>
              } />

              {/* Customers - accessible to all users */}
              <Route path="/customers" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <CustomersPage />
                </RouteGuard>
              } />
              <Route path="/customers/add" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <AddCustomerPage />
                </RouteGuard>
              } />
              <Route path="/customers/:customerId/edit" element={
                <RouteGuard permission={PERMISSIONS.VIEW_ALL_LEADS}>
                  <EditCustomerPage />
                </RouteGuard>
              } />

              {/* Vehicles - accessible to dealer and above */}
              <Route path="/products" element={
                <RouteGuard requireVehicleManagement>
                  <VehiclesPage />
                </RouteGuard>
              } />

              {/* Vehicles - Add/Edit Pages */}
              {/* Level 1: Brands */}
              <Route path="/vehicles/brands/add" element={
                <RouteGuard requireVehicleManagement>
                  <AddVehicleBrandPage />
                </RouteGuard>
              } />
              <Route path="/vehicles/brands/:brandId/edit" element={
                <RouteGuard requireVehicleManagement>
                  <EditVehicleBrandPage />
                </RouteGuard>
              } />
              {/* Level 2: Models */}
              <Route path="/vehicles/brands/:brandId/models/add" element={
                <RouteGuard requireVehicleManagement>
                  <AddVehicleModelPage />
                </RouteGuard>
              } />
              <Route path="/vehicles/brands/:brandId/models/:modelId/edit" element={
                <RouteGuard requireVehicleManagement>
                  <EditVehicleModelPage />
                </RouteGuard>
              } />
              {/* Level 3: Variants */}
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/add" element={
                <RouteGuard requireVehicleManagement>
                  <AddVehicleVariantPage />
                </RouteGuard>
              } />
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/:variantId/edit" element={
                <RouteGuard requireVehicleManagement>
                  <EditVehicleVariantPage />
                </RouteGuard>
              } />
              {/* Level 4: Colors */}
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/:variantId/colors/add" element={
                <RouteGuard requireVehicleManagement>
                  <AddVehicleColorPage />
                </RouteGuard>
              } />
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/:variantId/colors/:colorId/edit" element={
                <RouteGuard requireVehicleManagement>
                  <EditVehicleColorPage />
                </RouteGuard>
              } />
              {/* Level 5: States */}
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/:variantId/colors/:colorId/states/add" element={
                <RouteGuard requireVehicleManagement>
                  <AddVehicleStatePage />
                </RouteGuard>
              } />
              <Route path="/vehicles/brands/:brandId/models/:modelId/variants/:variantId/colors/:colorId/states/:stateId/edit" element={
                <RouteGuard requireVehicleManagement>
                  <EditVehicleStatePage />
                </RouteGuard>
              } />

              {/* Dealers - only accessible to super admin */}
              <Route path="/dealers" element={
                <RouteGuard requireDealerManagement>
                  <DealerManagementPage />
                </RouteGuard>
              } />
              <Route path="/dealers/add" element={
                <RouteGuard requireDealerManagement>
                  <AddDealerPage />
                </RouteGuard>
              } />
              <Route path="/dealers/:dealerId/edit" element={
                <RouteGuard requireDealerManagement>
                  <EditDealerPage />
                </RouteGuard>
              } />

              {/* Pricing - accessible to dealer and above */}
              <Route path="/pricing" element={
                <RouteGuard permission={PERMISSIONS.VIEW_PRICING}>
                  <PricingPage />
                </RouteGuard>
              } />
              <Route path="/pricing/edit" element={
                <RouteGuard permission={PERMISSIONS.VIEW_PRICING}>
                  <EditPricePage />
                </RouteGuard>
              } />

              {/* Team - accessible to dealers only */}
              <Route path="/team" element={
                <RouteGuard requireTeamManagement>
                  <TeamPage />
                </RouteGuard>
              } />
              <Route path="/team/add" element={
                <RouteGuard requireTeamManagement>
                  <AddMemberPage />
                </RouteGuard>
              } />
              <Route path="/team/:memberId/edit" element={
                <RouteGuard requireTeamManagement>
                  <EditMemberPage />
                </RouteGuard>
              } />

              {/* Accessories - accessible to admin and dealers */}
              <Route path="/accessories" element={
                <RouteGuard requireDealerOrAbove>
                  <AccessoriesPage />
                </RouteGuard>
              } />

              {/* Accessories - Add/Edit Pages */}
              <Route path="/accessories/brands/add" element={
                <RouteGuard requireDealerOrAbove>
                  <AddBrandPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/edit" element={
                <RouteGuard requireDealerOrAbove>
                  <EditBrandPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/add" element={
                <RouteGuard requireDealerOrAbove>
                  <AddCategoryPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/:categoryId/edit" element={
                <RouteGuard requireDealerOrAbove>
                  <EditCategoryPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/:categoryId/subcategories/add" element={
                <RouteGuard requireDealerOrAbove>
                  <AddSubcategoryPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/:categoryId/subcategories/:subcategoryId/edit" element={
                <RouteGuard requireDealerOrAbove>
                  <EditSubcategoryPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/:categoryId/subcategories/:subcategoryId/products/add" element={
                <RouteGuard requireDealerOrAbove>
                  <AddProductPage />
                </RouteGuard>
              } />
              <Route path="/accessories/brands/:brandId/categories/:categoryId/subcategories/:subcategoryId/products/:productId/edit" element={
                <RouteGuard requireDealerOrAbove>
                  <EditProductPage />
                </RouteGuard>
              } />

              {/* Accessories (Dealer Inventory) - accessible to dealers only */}
              <Route path="/dealer-inventory" element={
                <RouteGuard requireDealer>
                  <DealerInventoryPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/add" element={
                <RouteGuard requireDealer>
                  <AddInventoryPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/:inventoryId/edit" element={
                <RouteGuard requireDealer>
                  <EditInventoryPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders" element={
                <RouteGuard requireDealer>
                  <PurchaseOrdersListPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders/add" element={
                <RouteGuard requireDealer>
                  <AddEditPurchaseOrderPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders/:poId/edit" element={
                <RouteGuard requireDealer>
                  <AddEditPurchaseOrderPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders/:poId/view" element={
                <RouteGuard requireDealer>
                  <ViewPurchaseOrderPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders/:poId/receive" element={
                <RouteGuard requireDealer>
                  <ReceivePurchaseOrderPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/purchase-orders/:poId/convert-to-bill" element={
                <RouteGuard requireDealer>
                  <ConvertToBillPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/bills" element={
                <RouteGuard requireDealer>
                  <BillsListPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/bills/:billId/view" element={
                <RouteGuard requireDealer>
                  <ViewBillPage />
                </RouteGuard>
              } />
              <Route path="/dealer-inventory/bills/:billId/payment" element={
                <RouteGuard requireDealer>
                  <RecordPaymentPage />
                </RouteGuard>
              } />

              {/* Purchase Orders - accessible to dealers only */}
              <Route path="/purchase-orders" element={
                <RouteGuard requireDealer>
                  <PurchaseOrdersPage />
                </RouteGuard>
              } />
              <Route path="/purchase-orders/add" element={
                <RouteGuard requireDealer>
                  <AddPurchaseOrderPage />
                </RouteGuard>
              } />
              <Route path="/purchase-orders/:poId/edit" element={
                <RouteGuard requireDealer>
                  <EditPurchaseOrderPage />
                </RouteGuard>
              } />

              {/* Stock - accessible to dealers only */}
              <Route path="/vehicle-inventory" element={
                <RouteGuard requireDealer>
                  <VehicleInventoryPage />
                </RouteGuard>
              } />

              {/* Add/Edit Stock - accessible to dealers only */}
              <Route path="/vehicle-inventory/add" element={
                <RouteGuard requireDealer>
                  <AddStockPage />
                </RouteGuard>
              } />

              {/* Banking Management - accessible to dealers only */}
              <Route path="/banking" element={
                <RouteGuard requireDealer>
                  <BankingManagementPage />
                </RouteGuard>
              } />
              <Route path="/banking/accounts/add" element={
                <RouteGuard requireDealer>
                  <AddAccountPage />
                </RouteGuard>
              } />
              <Route path="/banking/accounts/:accountId/edit" element={
                <RouteGuard requireDealer>
                  <EditAccountPage />
                </RouteGuard>
              } />
              <Route path="/banking/transactions/add" element={
                <RouteGuard requireDealer>
                  <AddTransactionPage />
                </RouteGuard>
              } />
              <Route path="/banking/transactions/:transactionId/edit" element={
                <RouteGuard requireDealer>
                  <EditTransactionPage />
                </RouteGuard>
              } />

              {/* Bank Management - accessible to admin only */}
              <Route path="/bank-management" element={
                <RouteGuard requireAdmin>
                  <BankManagementPage />
                </RouteGuard>
              } />

              {/* Settings - accessible to admin only */}
              <Route path="/settings" element={
                <RouteGuard requireAdmin>
                  <SettingsPage />
                </RouteGuard>
              } />
              <Route path="/settings/pincodes/add" element={
                <RouteGuard requireAdmin>
                  <AddPincodePage />
                </RouteGuard>
              } />
              <Route path="/settings/pincodes/:pincodeId/edit" element={
                <RouteGuard requireAdmin>
                  <EditPincodePage />
                </RouteGuard>
              } />

              {/* Settings - Registration Charges */}
              <Route path="/settings/registration-charges/add" element={
                <RouteGuard requireAdmin>
                  <AddRegistrationChargePage />
                </RouteGuard>
              } />
              <Route path="/settings/registration-charges/:id/edit" element={
                <RouteGuard requireAdmin>
                  <EditRegistrationChargePage />
                </RouteGuard>
              } />

              {/* Settings - HSN */}
              <Route path="/settings/hsn/add" element={
                <RouteGuard requireAdmin>
                  <AddHSNPage />
                </RouteGuard>
              } />
              <Route path="/settings/hsn/:id/edit" element={
                <RouteGuard requireAdmin>
                  <EditHSNPage />
                </RouteGuard>
              } />

              {/* Profile - accessible to all authenticated users */}
              <Route path="/profile" element={
                <RouteGuard>
                  <ProfilePage />
                </RouteGuard>
              } />

              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </Content>
      </AppLayout>
    </ErrorBoundary>
  )
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  )
}

export default App
